import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { useState } from 'react';
import QuotePopup from '@/pages/home/components/QuotePopup';

export default function LegalPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#terms') {
      setTimeout(() => {
        const el = document.getElementById('terms');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen font-sans bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* Hero */}
      <section className="pt-40 pb-16" style={{ backgroundColor: '#0a2342' }}>
        <div className="max-w-[860px] mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
            <i className="ri-shield-check-line" style={{ color: '#7eb8f7' }} />
            Legal Documents
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Privacy &amp; Terms</h1>
          <p className="text-white/60 text-base max-w-xl mx-auto">
            Steel Link Global is committed to protecting your data and being transparent about how we operate. Please read these policies carefully.
          </p>
          <p className="text-white/40 text-xs mt-4">Last updated: April 3, 2026</p>
        </div>
      </section>

      {/* Tab nav */}
      <div className="sticky top-20 z-30 bg-white border-b border-gray-100">
        <div className="max-w-[860px] mx-auto px-6 flex gap-6">
          <a
            href="/legal"
            className="py-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap"
            style={{ borderColor: location.hash !== '#terms' ? '#0a2342' : 'transparent', color: location.hash !== '#terms' ? '#0a2342' : '#6b7280' }}
          >
            Privacy Policy
          </a>
          <a
            href="/legal#terms"
            className="py-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap"
            style={{ borderColor: location.hash === '#terms' ? '#0a2342' : 'transparent', color: location.hash === '#terms' ? '#0a2342' : '#6b7280' }}
          >
            Terms of Service
          </a>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-[860px] mx-auto px-6 py-16 space-y-16">

        {/* Privacy Policy */}
        <section id="privacy">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: '#e8f0fb' }}>
              <i className="ri-lock-line text-lg" style={{ color: '#0a2342' }} />
            </div>
            <h2 className="text-2xl font-black" style={{ color: '#0a2342' }}>Privacy Policy</h2>
          </div>

          <div className="space-y-8 text-gray-600 text-sm leading-relaxed">
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">1. Who We Are</h3>
              <p>
                Steel Link Global is a China-based steel trading company registered in Guangzhou, Guangdong Province, China. We operate the website at <strong>steelink.com</strong> and related subdomains. Our registered address is Room 906, Zhanghe Shengde Building, Huangpu Avenue, Tianhe District, Guangzhou City, Guangdong Province, China.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">2. Information We Collect</h3>
              <p className="mb-3">We collect information you provide directly to us, including:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Name, company name, job title</li>
                <li>Email address and phone number (including WhatsApp)</li>
                <li>Shipping destination, port of discharge, and country</li>
                <li>Product inquiries, quantities, and specifications</li>
                <li>Payment method preferences (L/C, T/T, etc.)</li>
                <li>IP address and browser information (collected automatically)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">3. How We Use Your Information</h3>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Respond to your quote requests and inquiries</li>
                <li>Prepare and send commercial invoices, packing lists, and shipping documents</li>
                <li>Communicate with you via email, phone, or WhatsApp about your orders</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations under Chinese law and international trade regulations</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">4. Data Sharing</h3>
              <p>
                We do not sell, rent, or trade your personal information to third parties. We may share your information with:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 mt-3">
                <li><strong>Freight forwarders and shipping agents</strong> — to arrange logistics and customs clearance</li>
                <li><strong>Mill partners</strong> — only the product specifications required for production</li>
                <li><strong>Inspection agencies (SGS, TUV)</strong> — when third-party inspection is requested</li>
                <li><strong>Banks and financial institutions</strong> — for L/C processing and payment verification</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">5. Data Retention</h3>
              <p>
                We retain your personal data for as long as necessary to fulfill the purposes outlined in this policy, or as required by applicable law. Trade documents and correspondence are typically retained for 7 years in accordance with Chinese accounting and trade regulations.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">6. Cookies</h3>
              <p>
                Our website uses essential cookies to ensure basic functionality. We do not use tracking or advertising cookies. You can disable cookies in your browser settings, though this may affect some website features.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">7. Your Rights</h3>
              <p className="mb-3">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data (subject to legal retention requirements)</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="mt-3">To exercise these rights, contact us at <a href="mailto:support@steelink.com" className="font-semibold hover:underline" style={{ color: '#0a2342' }}>support@steelink.com</a>.</p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">8. Security</h3>
              <p>
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. All data transmissions on our website are encrypted via HTTPS.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">9. Contact</h3>
              <p>
                For any privacy-related questions, please contact our team at <a href="mailto:support@steelink.com" className="font-semibold hover:underline" style={{ color: '#0a2342' }}>support@steelink.com</a> or via WhatsApp at <a href="https://wa.me/8617671262710" className="font-semibold hover:underline" style={{ color: '#0a2342' }}>+86 17671262710</a>.
              </p>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t-2 border-dashed border-gray-200" />

        {/* Terms of Service */}
        <section id="terms">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: '#e8f0fb' }}>
              <i className="ri-file-text-line text-lg" style={{ color: '#0a2342' }} />
            </div>
            <h2 className="text-2xl font-black" style={{ color: '#0a2342' }}>Terms of Service</h2>
          </div>

          <div className="space-y-8 text-gray-600 text-sm leading-relaxed">
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">1. Acceptance of Terms</h3>
              <p>
                By accessing or using the Steel Link Global website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">2. Services Provided</h3>
              <p>
                Steel Link Global provides steel product sourcing, trading, and export services from China. Our services include product quotation, order processing, quality inspection coordination, shipping arrangement, and documentation support. All prices quoted are in USD unless otherwise stated and are valid for 7 days from the date of quotation.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">3. Quotations and Orders</h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>All quotations are subject to mill availability and current market prices</li>
                <li>Orders are confirmed only upon receipt of a signed Sales Contract and deposit payment</li>
                <li>Minimum order quantities (MOQ) apply per product and are stated in each quotation</li>
                <li>Steel prices are subject to market fluctuation; quoted prices are valid for 7 calendar days</li>
                <li>We reserve the right to decline any order at our discretion</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">4. Payment Terms</h3>
              <p className="mb-3">Standard payment terms are:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong>T/T (Telegraphic Transfer):</strong> 30% deposit upon contract signing, 70% balance before shipment</li>
                <li><strong>L/C (Letter of Credit):</strong> Irrevocable L/C at sight from a first-class bank, subject to prior approval</li>
                <li>All bank charges outside China are the buyer&apos;s responsibility</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">5. Shipping and Delivery</h3>
              <p>
                All goods are shipped FOB (Free On Board) from major Chinese ports including Shanghai, Tianjin, Guangzhou, Qingdao, and Ningbo unless otherwise agreed. Risk of loss passes to the buyer upon loading at the port of origin. Delivery timelines are estimates and may vary due to production schedules, port congestion, or force majeure events.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">6. Quality and Inspection</h3>
              <p>
                All products are supplied with mill test certificates (MTC) conforming to the specified standard (GB, ASTM, EN, JIS, etc.). Third-party inspection by SGS, TUV, or BV is available upon request at the buyer&apos;s cost. Claims regarding quality must be submitted within 30 days of arrival at the destination port, accompanied by an independent inspection report.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">7. Limitation of Liability</h3>
              <p>
                Steel Link Global&apos;s liability is limited to the value of the goods supplied under the relevant contract. We are not liable for indirect, consequential, or incidental damages arising from delays, defects, or non-delivery caused by circumstances beyond our reasonable control, including but not limited to force majeure events, port strikes, or shipping line disruptions.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">8. Governing Law</h3>
              <p>
                These terms are governed by the laws of the People&apos;s Republic of China. Any disputes arising from these terms or our services shall be resolved through friendly negotiation. If negotiation fails, disputes shall be submitted to the Guangzhou Arbitration Commission for arbitration in accordance with its rules.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">9. Changes to Terms</h3>
              <p>
                We reserve the right to update these Terms of Service at any time. Changes will be posted on this page with an updated date. Continued use of our services after changes constitutes acceptance of the new terms.
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">10. Contact</h3>
              <p>
                For any questions about these terms, contact us at <a href="mailto:support@steelink.com" className="font-semibold hover:underline" style={{ color: '#0a2342' }}>support@steelink.com</a>.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: '#0a2342' }}>
          <h3 className="text-white font-black text-xl mb-2">Ready to place an order?</h3>
          <p className="text-white/60 text-sm mb-6">Get a free quote from our team within 2 hours.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/quote"
              className="px-8 py-3 bg-white font-black text-sm rounded-lg cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
              style={{ color: '#0a2342' }}
            >
              <i className="ri-file-list-3-line mr-2" />Get a Free Quote
            </a>
            <a
              href="https://wa.me/8617671262710"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-black text-sm rounded-lg cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-whatsapp-line" />WhatsApp Us
            </a>
          </div>
        </div>
      </main>

      <FooterSection />
      <WhatsAppButton />
      {quoteOpen && <QuotePopup onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}
